/**
 * MunicipalitySearch - Buscador de municipios andaluces
 * 
 * F8.02 - Controles interactivos del visor Leaflet
 * 
 * Búsqueda fuzzy sobre los 785 municipios andaluces con centroides UTM.
 * Al seleccionar un municipio, centra el mapa en su cabecera municipal.
 * 
 * @module components/map/controls/MunicipalitySearch
 */

import React, { memo, useCallback, useRef, useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import { CENTROIDES_MUNICIPIOS, type CentroideMunicipio } from '../../../lib/municipiosCentroides';
import { toWGS84 } from '../../../lib/leaflet-crs';
import { MUNICIPAL_ZOOM } from '../constants';

// =============================================================================
// TIPOS
// =============================================================================

export interface MunicipalityOption {
  /** Código INE de 5 dígitos */
  ine: string;
  /** Nombre del municipio */
  nombre: string;
  /** Provincia */
  provincia: string;
  /** Centroide UTM X */
  x: number;
  /** Centroide UTM Y */
  y: number;
}

export interface MunicipalitySearchProps {
  /** Posición del buscador */
  position?: 'topleft' | 'topright';
  /** Zoom al seleccionar municipio (default: MUNICIPAL_ZOOM) */
  flyToZoom?: number;
  /** Máximo de resultados a mostrar */
  maxResults?: number;
  /** Placeholder del input */
  placeholder?: string;
  /** Callback al seleccionar municipio */
  onSelect?: (municipality: MunicipalityOption) => void;
  /** Clases CSS adicionales */
  className?: string;
}

// =============================================================================
// DATOS PRE-COMPUTADOS
// =============================================================================

/** Lista plana de municipios para búsqueda rápida */
const MUNICIPALITY_LIST: MunicipalityOption[] = Object.entries(CENTROIDES_MUNICIPIOS).map(
  ([ine, data]: [string, CentroideMunicipio]) => ({
    ine,
    nombre: data.nombre,
    provincia: data.provincia,
    x: data.x,
    y: data.y,
  })
);

/** Normaliza texto para búsqueda (sin acentos, minúsculas) */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Cache de nombres normalizados */
const NORMALIZED_NAMES = new Map<string, string>();
for (const m of MUNICIPALITY_LIST) {
  NORMALIZED_NAMES.set(m.ine, normalize(m.nombre));
}

// =============================================================================
// COMPONENTE
// =============================================================================

export const MunicipalitySearch = memo<MunicipalitySearchProps>(function MunicipalitySearch({
  position = 'topleft',
  flyToZoom = MUNICIPAL_ZOOM,
  maxResults = 6,
  placeholder = 'Buscar municipio…',
  onSelect,
  className = '',
}) {
  const map = useMap();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MunicipalityOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const _listRef = useRef<HTMLUListElement>(null);

  // Evitar propagación de eventos al mapa
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    el.addEventListener('dblclick', stop);
    el.addEventListener('mousedown', stop);
    el.addEventListener('wheel', stop);
    return () => {
      el.removeEventListener('dblclick', stop);
      el.removeEventListener('mousedown', stop);
      el.removeEventListener('wheel', stop);
    };
  }, []);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Búsqueda fuzzy simple por prefijo + contenido
  const handleSearch = useCallback((value: string) => {
    setQuery(value);
    setActiveIndex(-1);

    if (value.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const normalizedQuery = normalize(value);
    const matches: MunicipalityOption[] = [];

    // Primero: coincidencias por prefijo
    for (const m of MUNICIPALITY_LIST) {
      const normalizedName = NORMALIZED_NAMES.get(m.ine) || '';
      if (normalizedName.startsWith(normalizedQuery)) {
        matches.push(m);
        if (matches.length >= maxResults) break;
      }
    }

    // Si no hay suficientes, buscar contenido
    if (matches.length < maxResults) {
      for (const m of MUNICIPALITY_LIST) {
        if (matches.some(existing => existing.ine === m.ine)) continue;
        const normalizedName = NORMALIZED_NAMES.get(m.ine) || '';
        if (normalizedName.includes(normalizedQuery)) {
          matches.push(m);
          if (matches.length >= maxResults) break;
        }
      }
    }

    setResults(matches);
    setIsOpen(matches.length > 0);
  }, [maxResults]);

  // Seleccionar municipio → flyTo
  const handleSelectMunicipality = useCallback((municipality: MunicipalityOption) => {
    const [lat, lng] = toWGS84(municipality.x, municipality.y);
    map.flyTo([lat, lng], flyToZoom, { duration: 0.8 });

    setQuery(municipality.nombre);
    setIsOpen(false);
    setActiveIndex(-1);
    onSelect?.(municipality);
  }, [map, flyToZoom, onSelect]);

  // Navegación por teclado
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) {
      if (e.key === 'Escape') {
        setQuery('');
        setResults([]);
        setIsOpen(false);
        inputRef.current?.blur();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < results.length) {
          handleSelectMunicipality(results[activeIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  }, [isOpen, results, activeIndex, handleSelectMunicipality]);

  // Limpiar búsqueda
  const handleClear = useCallback(() => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  }, []);

  // Posición CSS
  const posStyle: React.CSSProperties = {
    position: 'absolute',
    zIndex: 1001,
    top: 10,
    ...(position === 'topright' ? { right: 10 } : { left: 10 }),
  };

  return (
    <div
      ref={containerRef}
      className={`ptel-muni-search ${className}`}
      style={posStyle}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-label="Buscar municipio andaluz"
    >
      <div className="ptel-muni-search__input-wrapper">
        <svg className="ptel-muni-search__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10.5 10.5L14.5 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="ptel-muni-search__input"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0) setIsOpen(true); }}
          placeholder={placeholder}
          aria-label="Nombre del municipio"
          aria-autocomplete="list"
          aria-controls="ptel-muni-results"
          aria-activedescendant={activeIndex >= 0 ? `muni-option-${activeIndex}` : undefined}
          autoComplete="off"
          spellCheck={false}
        />
        {query && (
          <button
            className="ptel-muni-search__clear"
            onClick={handleClear}
            aria-label="Limpiar búsqueda"
            tabIndex={-1}
          >
            ×
          </button>
        )}
      </div>

      {/* Dropdown de resultados */}
      {isOpen && results.length > 0 && (
        <ul
          id="ptel-muni-results"
          className="ptel-muni-search__results"
          role="listbox"
          aria-label="Municipios encontrados"
        >
          {results.map((m, index) => (
            <li
              key={m.ine}
              id={`muni-option-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={`ptel-muni-search__option ${index === activeIndex ? 'ptel-muni-search__option--active' : ''}`}
              onClick={() => handleSelectMunicipality(m)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <span className="ptel-muni-search__option-name">{m.nombre}</span>
              <span className="ptel-muni-search__option-prov">{m.provincia}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default MunicipalitySearch;
